import React, { useState, useEffect, useRef } from "react";
import { Calendar, Clock, ClockIcon, Link2, Video } from "lucide-react";
import "./TaskMeetingSection.css";
import ViewEvent from "../calendar/events/ViewEvent";
import ModalMeet from "../calendar/ModalMeet";
import { AllEvent } from "../../../interfaces/identity/calendar/Events";
import AvatarImage from "../../ui/Avatar/AvatarImage";
import moment from "moment-timezone";
import UserAvatar from "../calendar/UserAvatar";
import { OverlayTrigger, Tooltip, Button } from "react-bootstrap";
import AvatarGroupTask from "../TasksPage/AvatarGroupTask";
import { useAvatarCache } from "../../../contexts/AvatarContext";
import { getAllEmployees } from "../../../services/calendarService/EventService";
import { useAuth } from "../../../firebase/AuthProvider";
import NoDataFound from "./NoDataFound";

interface TaskMeetingSectionProps {
  task: any;
  taskId: string;
  reload: () => void;
  participantId?: string;
  meetings?: AllEvent[] | [];
}

// Component to render meeting attendees using AvatarGroupTask
const MeetingAttendeesAvatars = ({ meeting, allUserIds }: { meeting: AllEvent; allUserIds: string[] }) => {
  const { getAvatarUrl } = useAvatarCache();
  const { currentUser } = useAuth();
  const [avatarData, setAvatarData] = useState<Array<{ id: string; name: string; image?: string }>>([]);
  const [employees, setEmployees] = useState<any[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);

  // Fetch employees list
  useEffect(() => {
    const fetchEmployees = async () => {
      if (!currentUser) return;
      try {
        const token = await currentUser.getIdToken();
        const response = await getAllEmployees(token);
        if (response) {
          setEmployees(response);
        }
      } catch (error) {
        console.error('Error fetching employees:', error);
      }
    };
    fetchEmployees();
  }, [currentUser]);

  useEffect(() => {
    const loadAvatars = async () => {
      const avatars = await Promise.all(
        allUserIds.map(async (userId) => {
          // Use ownerName for owner
          let name: string;
          if (userId === meeting.ownerUserID) {
            name = meeting.ownerName || userId;
          } else {
            // Find employee by userId to get firstName and lastName
            const employee = employees.find((emp) => emp.userId === userId);
            if (employee) {
              // Check if firstName and lastName exist (they might be in the response even if not in interface)
              const firstName = (employee as any).firstName;
              const lastName = (employee as any).lastName;
              
              if (firstName && lastName) {
                name = `${firstName} ${lastName}`;
              } else if (employee.userName) {
                name = employee.userName;
              } else {
                name = userId; // Fallback to userId
              }
            } else {
              name = userId; // Fallback to userId if employee not found
            }
          }
          
          let image: string | undefined;
          try {
            const url = await getAvatarUrl(userId);
            image = url || undefined;
          } catch {
            image = undefined;
          }

          return { id: userId, name, image };
        })
      );
      setAvatarData(avatars);
    };

    if (allUserIds.length > 0 && employees.length > 0) {
      loadAvatars();
    } else if (allUserIds.length > 0) {
      // Still load avatars even if employees not loaded yet (will show userId temporarily)
      loadAvatars();
    }
  }, [allUserIds, meeting.ownerUserID, meeting.ownerName, employees, getAvatarUrl]);

  // Fix dropdown positioning by converting absolute to fixed when inside overflow container
  useEffect(() => {
    if (!containerRef.current) return;

    const fixDropdownPosition = () => {
      const dropdown = containerRef.current?.querySelector('.absolute.right-0') as HTMLElement;
      if (dropdown) {
        const button = containerRef.current?.querySelector('button[class*="rounded-full"]') as HTMLElement;
        if (button) {
          const rect = button.getBoundingClientRect();
          const dropdownHeight = dropdown.offsetHeight || 256; // Default max height + padding
          const viewportHeight = window.innerHeight;
          const spaceBelow = viewportHeight - rect.bottom;
          const spaceAbove = rect.top;
          
          dropdown.style.position = 'fixed';
          dropdown.style.right = 'auto';
          dropdown.style.zIndex = '9999';
          
          // Check if dropdown would overflow bottom of viewport
          if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
            // Show above the button
            dropdown.style.bottom = `${viewportHeight - rect.top + 8}px`;
            dropdown.style.top = 'auto';
          } else {
            // Show below the button (default)
            dropdown.style.top = `${rect.bottom + 8}px`;
            dropdown.style.bottom = 'auto';
          }
          
          // Position from left edge of button, aligned to button's left
          dropdown.style.left = `${rect.left}px`;
        }
      }
    };

    // Use MutationObserver to detect when dropdown appears
    const observer = new MutationObserver(fixDropdownPosition);
    observer.observe(containerRef.current, { childList: true, subtree: true });

    // Also check on interval as fallback
    const interval = setInterval(fixDropdownPosition, 100);

    return () => {
      observer.disconnect();
      clearInterval(interval);
    };
  }, [avatarData]);

  return (
    <div ref={containerRef} className="meeting-attendees-avatar-wrapper">
      <AvatarGroupTask
        avatars={avatarData}
        selectedIds={[]}
        onChange={() => {}}
        maxVisible={3}
        viewOnly={true}
      />
    </div>
  );
};

const TaskMeetingSection = ({
  task,
  taskId,
  reload,
  meetings,
  participantId,
}: TaskMeetingSectionProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState<AllEvent | null>(null);

  // Update selectedMeeting when meetings prop changes (after accept/reject)
  // This ensures the modal shows the latest meeting data
  useEffect(() => {
    if (selectedMeeting && meetings) {
      const updatedMeeting = meetings.find(m => m.id === selectedMeeting.id);
      if (updatedMeeting) {
        // Update selectedMeeting with latest data (e.g., isInvitationAccept changed)
        setSelectedMeeting(updatedMeeting);
      } else {
        // Meeting was removed (rejected and filtered out), close modal
        if (isOpen) {
          setIsOpen(false);
          setSelectedMeeting(null);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetings]);

  // Function to determine border color - using ownerColor from EventCard logic
  const getMeetingBorderColor = (meeting: AllEvent): string => {
    // Use ownerColor if available, otherwise default
    return meeting.ownerColor || "#FF8904";
  };

  const handleJoinMeeting = (link: string) => {
    window.open(link, "_blank");
  };
  const getInitials = (name: string) => {
    if (!name) return "?";

    const clean = name.trim().replace(/\s+/g, " ");
    const words = clean.split(" ");

    // ID / random string → only first letter
    if (words.length === 1) {
      return clean.charAt(0).toUpperCase();
    }

    // Normal name → first letter of first two words
    return (words[0][0] + words[1][0]).toUpperCase();
  };

  const handleClickJoin = (meetingLink: string, disabled: boolean) => {
    if (disabled) return;
    handleJoinMeeting(meetingLink);
  };
  const formatRemainingTime = (ms: number) => {
    const duration = moment.duration(ms);
    const hours = Math.floor(duration.asHours());
    const minutes = duration.minutes();
    const seconds = duration.seconds();
    let parts: string[] = [];
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);
    parts.push(`${seconds}s`);
    return parts.join(" ");
  };

  // Filter out rejected meetings (same logic as MyCalendar)
  // Rejected meetings have isInvitationAccept === false
  const filteredMeetings = meetings?.filter((meeting) => {
    // Show meetings that are:
    // - Accepted (isInvitationAccept === true)
    // - Pending (isInvitationAccept === null or undefined)
    // - Don't have the property (undefined)
    // Exclude rejected (isInvitationAccept === false)
    return meeting.isInvitationAccept !== false;
  }) || [];

  return (
    <div className="task-meeting-section ">
      <div className="meeting-list">
        {filteredMeetings && filteredMeetings.length > 0 ? (
          filteredMeetings.map((meeting) => {
            // Get all unique user IDs (same logic as EventCard expects)
            const allUserIds = [
              // (meeting as any).createdby || meeting.ownerUserID,
              ...(meeting.participantUserIds || [])
            ].filter((id, index, self) => id && self.indexOf(id) === index);

            const displayUserIds = allUserIds.slice(0, 4);
            const borderColor = getMeetingBorderColor(meeting);

            // Backend returns date in format: 2026-01-16T20:45:00.000Z (UTC format)
            // Backend stores the exact time we sent in UTC format
            // We need to parse it as UTC and convert to user's local timezone for display
            const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            
            // Parse the UTC datetime string and convert to user's local timezone
            // Format: 2026-01-16T20:45:00.000Z
            const formattedStartTime = meeting.startTime
              ? moment.utc(meeting.startTime).local().format("hh:mm A")
              : "";
            const formattedEndTime = meeting.endTime
              ? moment.utc(meeting.endTime).local().format("hh:mm A")
              : "";

            // Format date - format: "dddd, MMM DD" (e.g., "Wednesday, Dec 01")
            const formattedDate = meeting.startTime
              ? moment.utc(meeting.startTime).local().format("dddd, MMM DD")
              : "";

            // Enable Join only within 30 minutes before start until meeting end (same rule as ViewEvent)
            // Parse as UTC and convert to user's local timezone for comparison
            const startMoment = meeting.startTime ? moment.utc(meeting.startTime).local() : null;
            const endMoment = meeting.endTime ? moment.utc(meeting.endTime).local() : null;
            const now = moment();
            const preJoinStart = startMoment ? startMoment.clone().subtract(30, "minutes") : null;
            const canJoin =
              !!startMoment &&
              !!endMoment &&
              now.isSameOrAfter(preJoinStart as moment.Moment) &&
              now.isSameOrBefore(endMoment as moment.Moment);
            let tooltipMessage = "Joining opens 30 min before start.";
            if (startMoment && preJoinStart) {
              const msUntilPrejoin = preJoinStart.diff(now);
              if (msUntilPrejoin > 0 && msUntilPrejoin <= 60 * 60 * 1000) {
                tooltipMessage = `You can join in ${formatRemainingTime(msUntilPrejoin)}`;
              } else if (msUntilPrejoin <= 0 && !canJoin) {
                tooltipMessage = "Meeting already ended.";
              }
            }

            return (
              <div
                key={meeting.id}
                onClick={() => {
                  // Ensure the meeting object has the correct structure for ViewEvent
                  // Match MyCalendar's logic: use originalEventId if it exists, otherwise use id
                  // Ensure id is always a string (API expects calendarEventId as string)
                  const eventToPass: AllEvent = {
                    ...meeting,
                    // Convert id to string if it's not already (API expects string)
                    id: meeting.id ? String(meeting.id) : meeting.id,
                  };
                  setSelectedMeeting(eventToPass);
                  setIsOpen(true);
                }}
                className="meeting-card cursor-pointer"
                style={{ borderLeftColor: borderColor, borderLeftWidth: "4.62px", borderLeftStyle: "solid" }}
              >
                <div className="meeting-header w-[35%]">
                  <h3 className="meeting-title">{meeting.title || "-"}</h3>
                  {/* <div className="meeting-time">
                    <Clock size={13} />
                    <span>
                      {formattedStartTime} - {formattedEndTime}
                    </span>
                  </div>
                  <div className="meeting-attendees">
                    <div className="avatar-group">
                      {displayUserIds.map((userId, index) => (
                        <div
                          key={`${userId}-${index}`}
                          className="header_filter_img meeting-avatar-wrapper"
                        >
                          <AvatarImage
                            userId={userId}
                            size="xs"
                          />
                        </div>
                      ))}
                    </div>
                    <span className="attendee-count">
                      {allUserIds.length} attendees
                    </span>
                  </div> */}

                  <div className="flex items-center gap-2 meeting-time ">
                    {/* <Calendar size={13} /> */}
                    <Clock size={13} />
                    {formattedDate && (
                      <span className="meeting-time-span ">
                        {formattedDate}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center meeting-time gap-2 ">
                    <Clock size={13} visibility={"hidden"} />
                   
                    <span className="meeting-time-span ">
                      {formattedStartTime} - {formattedEndTime}
                    </span>
                  </div>


                  <div className="flex items-center flex-wrap gap-y-2 mt-1.5 mb-[-7px]">
                    <MeetingAttendeesAvatars
                      meeting={meeting}
                      allUserIds={allUserIds}
                    />
                    <span className="ml-2 meeting-attendees truncate">
                      {allUserIds.length} attendees
                    </span>
                  </div>
                </div>

                <div className="meeting-info">
                  <div className="meeting-description">
                    <div className="description-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 20 20" fill="none">
  <path d="M1.875 3.75C1.70924 3.75 1.55027 3.81585 1.43306 3.93306C1.31585 4.05027 1.25 4.20924 1.25 4.375C1.25 4.54076 1.31585 4.69973 1.43306 4.81694C1.55027 4.93415 1.70924 5 1.875 5H18.125C18.2908 5 18.4497 4.93415 18.5669 4.81694C18.6842 4.69973 18.75 4.54076 18.75 4.375C18.75 4.20924 18.6842 4.05027 18.5669 3.93306C18.4497 3.81585 18.2908 3.75 18.125 3.75H1.875ZM1.875 7.5C1.70924 7.5 1.55027 7.56585 1.43306 7.68306C1.31585 7.80027 1.25 7.95924 1.25 8.125C1.25 8.29076 1.31585 8.44973 1.43306 8.56694C1.55027 8.68415 1.70924 8.75 1.875 8.75H18.125C18.2908 8.75 18.4497 8.68415 18.5669 8.56694C18.6842 8.44973 18.75 8.29076 18.75 8.125C18.75 7.95924 18.6842 7.80027 18.5669 7.68306C18.4497 7.56585 18.2908 7.5 18.125 7.5H1.875ZM1.25 11.875C1.25 11.7092 1.31585 11.5503 1.43306 11.4331C1.55027 11.3158 1.70924 11.25 1.875 11.25H18.125C18.2908 11.25 18.4497 11.3158 18.5669 11.4331C18.6842 11.5503 18.75 11.7092 18.75 11.875C18.75 12.0408 18.6842 12.1997 18.5669 12.3169C18.4497 12.4342 18.2908 12.5 18.125 12.5H1.875C1.70924 12.5 1.55027 12.4342 1.43306 12.3169C1.31585 12.1997 1.25 12.0408 1.25 11.875ZM1.875 15C1.70924 15 1.55027 15.0658 1.43306 15.1831C1.31585 15.3003 1.25 15.4592 1.25 15.625C1.25 15.7908 1.31585 15.9497 1.43306 16.0669C1.55027 16.1842 1.70924 16.25 1.875 16.25H13.125C13.2908 16.25 13.4497 16.1842 13.5669 16.0669C13.6842 15.9497 13.75 15.7908 13.75 15.625C13.75 15.4592 13.6842 15.3003 13.5669 15.1831C13.4497 15.0658 13.2908 15 13.125 15H1.875Z" fill="black"/>
</svg>
                    </div>
                    <p>{meeting.description || "--"}</p>
                  </div>

                  {/* <div className="meeting-link">
                    <Link2 size={13} />
                    <a
                      href={meeting.location || meeting.meetingDetails}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="meeting-link-text"
                    >
                      {meeting.meetingDetails}
                    </a>
                  </div> */}
                  <div className="meeting-link  gap-2  !text-base">
                    {/* <Link2 size={14} rotate={70} color="black" style={{transform: "rotate(125deg)"}} /> */}
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 20 20" fill="none">
  <path d="M7.49952 12.5L12.4995 7.49999M9.16619 4.99999L9.55202 4.55332C10.3335 3.77193 11.3934 3.33299 12.4986 3.33307C13.6037 3.33315 14.6635 3.77224 15.4449 4.55374C16.2263 5.33524 16.6653 6.39515 16.6652 7.50028C16.6651 8.60542 16.226 9.66526 15.4445 10.4467L14.9995 10.8333M10.8329 15L10.502 15.445C9.71118 16.2264 8.64421 16.6646 7.53244 16.6646C6.42066 16.6646 5.35369 16.2264 4.56285 15.445C4.17295 15.0598 3.86338 14.601 3.6521 14.0952C3.44082 13.5895 3.33203 13.0468 3.33203 12.4987C3.33203 11.9506 3.44082 11.408 3.6521 10.9022C3.86338 10.3965 4.17295 9.93771 4.56285 9.55249L4.99952 9.16666" stroke="black" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
                    {meeting.location && meeting.location.length > 0 ? (
                      <p className="m-0">
                        {meeting.location}
                      </p>
                    ) : meeting.isSprout ? (
                      <p className="text-[#676767] break-all m-0"
                        title="Join via Waivera">
                        Join via Waivera
                      </p>
                    ) : (
                      <p className="m-0">--</p>
                    )}
                  </div>
                </div>

                <OverlayTrigger
                  placement="top"
                  overlay={
                    !canJoin ? (
                      <Tooltip id={`tooltip-join-${meeting.id}`}>{tooltipMessage}</Tooltip>
                    ) : (
                      <></>
                    )
                  }
                  trigger={['hover', 'focus', 'click']}
                >
                  <span className="d-inline-block ml-auto">
                    <Button
                      className="btn_flx join-button !text-xs !rounded-[10px]"
                      variant="primary"
                      onClick={() => handleClickJoin(meeting.meetingDetails, !canJoin)}
                      disabled={!canJoin}
                    >
                      <Video size={14} /> Join
                    </Button>
                  </span>
                </OverlayTrigger>
              </div>
            );
          })
        ) : (
          <NoDataFound 
            title="No Meetings Found"
            description="There are no meetings to show you right now"
          />
        )}
      </div>

      {selectedMeeting && (
        <ModalMeet isOpen={isOpen} onClose={() => { setIsOpen(false); setSelectedMeeting(null); }} widthClass={"max-w-[570px]"}>
          <ViewEvent
            event={{
              ...selectedMeeting,
              // Ensure id is properly formatted as string for API call
              // This matches how MyCalendar handles events
              id: selectedMeeting.id ? String(selectedMeeting.id) : selectedMeeting.id,
            }}
            onClose={() => { 
              setIsOpen(false); 
              setSelectedMeeting(null); 
            }}
            refreshAllEvents={() => {
              // Reload task data to get updated meetings
              // This will update the meetings prop, which will trigger the useEffect
              // to update selectedMeeting or close modal if meeting was rejected
              reload();
            }}
          />
        </ModalMeet>
      )}
    </div>
  );
};

export default TaskMeetingSection;
